import { CommentCheckIcon, HomeIcon, ReportIcon, ShieldIcon, ThumbsUpIcon, TipIcon } from "@hopper-ui/icons";
import { LogIcon, StethoscopeIcon } from "@sg-protect/components";
import { FeatureFlagKeys } from "@sg-protect/federation-runtime";
import {
    type ModuleRegisterFunction,
    NestedErrorBoundary,
    ProtectSidebarItemPriority,
    PublicLayout,
    registerProtectHeaderWidget,
    registerProtectSearchProvider,
    registerProtectSidebarItem
} from "@sg-protect/shell";
import { Outlet, redirect } from "react-router";
import { ActivityHeaderWidget } from "./components/activity-center/ActivityHeaderWidget.tsx";
import { ModuleAuthenticatedProviders } from "./components/layout/ModuleAuthenticatedProviders.tsx";
import { ModuleGlobalProviders } from "./components/layout/ModuleGlobalProviders.tsx";
import { peopleSearchProvider } from "./components/search/peopleSearchProvider.tsx";
import { workspacesSearchProvider } from "./components/search/workspacesSearchProvider.tsx";
import { getPaths } from "./paths.ts";
import { registerPrivateRoutes, registerPublicRoutes } from "./routes.tsx";

const registerRoutes: ModuleRegisterFunction = runtime => {
    runtime.registerRoute({
        path: "/app",
        element: (
            <ModuleGlobalProviders>
                <Outlet />
            </ModuleGlobalProviders>
        ),
        errorElement: <NestedErrorBoundary />,
        children: [
            {
                index: true,
                element: null,
                loader: () => redirect("/")
            },
            {
                element: (
                    <PublicLayout>
                        <Outlet />
                    </PublicLayout>
                ),
                children: registerPublicRoutes()
            },
            {
                element: (
                    <ModuleAuthenticatedProviders>
                        <Outlet />
                    </ModuleAuthenticatedProviders>
                ),
                children: registerPrivateRoutes(runtime)
            }
        ]
    });
};

const registerMswRoutes: ModuleRegisterFunction = runtime => {
    if (runtime.isMswEnabled) {
        runtime.registerRoute(
            {
                path: "/mock/consent/initiate",
                $visibility: "public",
                lazy: async () => {
                    const { MockConsentPage } = await import("./mocks/MockConsentPage.tsx");

                    return {
                        element: <MockConsentPage />
                    };
                }
            },
            { hoist: true }
        );
        runtime.registerRoute(
            {
                path: "/mock/microsoft/:tenantId/adminconsent",
                $visibility: "public",
                lazy: async () => {
                    const { MockAdminConsentPage } = await import("./mocks/MockAdminConsentPage.tsx");

                    return {
                        element: <MockAdminConsentPage />
                    };
                }
            },
            { hoist: true }
        );
    }
};

const registerMswRequestHandlers: ModuleRegisterFunction = async runtime => {
    if (runtime.isMswEnabled) {
        const { handlers: apiHandlers, scenarios } = await import("./api/handlers.ts");
        const { getEnabledScenarios, getScenarioHandlers } = await import("@sg-protect/core/mocks");

        const scenarioHandlers = getScenarioHandlers(scenarios, getEnabledScenarios());
        const requestHandlers = [...scenarioHandlers, ...apiHandlers];

        // Tags handlers so DUMP_MSW_MOCKS=true (pnpm dev-msw:dump) can report which module each mock came from.
        requestHandlers.forEach(handler => Object.assign(handler, { mockModule: "tenant-assessment" }));

        runtime.registerRequestHandlers(requestHandlers);
    }
};

export const register: ModuleRegisterFunction = async runtime => {
    registerProtectHeaderWidget({ id: "activity", component: ActivityHeaderWidget });
    registerProtectSearchProvider(workspacesSearchProvider);
    registerProtectSearchProvider(peopleSearchProvider);
    await registerMswRequestHandlers(runtime);
    await registerRoutes(runtime);
    await registerMswRoutes(runtime);

    return registerNavigationItems(runtime);
};

const registerNavigationItems: ModuleRegisterFunction = () => {
    // We register the navigation items in the deferred phase because we need to wait for the workspace to be loaded in order to get the correct paths with the workspace id
    return (deferredRuntime, { currentWorkspace }) => {
        const paths = currentWorkspace?.id ? getPaths({ workspaceId: currentWorkspace.id }) : getPaths();

        const isRecommendationsTabReleased = deferredRuntime.getFeatureFlag(
            FeatureFlagKeys.RecommendationsTabReleased,
            false
        );

        registerProtectSidebarItem(deferredRuntime, {
            id: "home",
            label: "Home",
            to: paths.home,
            icon: <HomeIcon size="md" />,
            priority: ProtectSidebarItemPriority.Overview
        });

        registerProtectSidebarItem(deferredRuntime, {
            id: "tenant-info",
            label: "Tenant info",
            to: paths.tenantInfo,
            icon: <StethoscopeIcon size="md" />,
            priority: ProtectSidebarItemPriority.TenantInfo
        });

        registerProtectSidebarItem(deferredRuntime, {
            id: "insights",
            label: "Insights",
            to: paths.insights,
            icon: <TipIcon size="md" />,
            priority: ProtectSidebarItemPriority.Insights
        });

        if (isRecommendationsTabReleased) {
            registerProtectSidebarItem(deferredRuntime, {
                id: "recommendations",
                label: "Recommendations",
                to: paths.recommendations,
                icon: <ThumbsUpIcon size="md" />,
                priority: ProtectSidebarItemPriority.Recommendations
            });
        }

        registerProtectSidebarItem(deferredRuntime, {
            id: "reporting",
            label: "Reports",
            to: paths.reports,
            icon: <ReportIcon size="md" />,
            priority: ProtectSidebarItemPriority.Reporting
        });

        registerProtectSidebarItem(deferredRuntime, {
            id: "policies",
            label: "Policies",
            to: paths.automations,
            icon: <ShieldIcon size="md" />,
            priority: ProtectSidebarItemPriority.Policies
        });

        registerProtectSidebarItem(deferredRuntime, {
            id: "reviews",
            label: "Reviews",
            to: paths.reviews,
            icon: <CommentCheckIcon size="md" />,
            priority: ProtectSidebarItemPriority.Reviews
        });

        registerProtectSidebarItem(deferredRuntime, {
            id: "activity-log",
            label: "Activity log",
            to: paths.activityLog,
            icon: <LogIcon size="md" />,
            priority: ProtectSidebarItemPriority.ActivityLog
        });
    };
};
